import { Component, findComponents, EventListener } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import eventBus, { SLEvent } from '@swisslife/sl-event-bus/src/sl-event-bus';

export type BackdropInteractionCallback = (event: string, trigger: IBackdropTrigger) => any;

export interface IBackdropTrigger {
	component: string;
	modifiers?: string[];
	settings?: any;
	onInteraction: BackdropInteractionCallback;
}

export interface IBackdropNativeEvent {
	eventData: IBackdropTrigger;
}

export type BackdropStack = IBackdropTrigger[];

export const ANONYMOUS_BACKDROP_TRIGGER: IBackdropTrigger = {
	component: 'anonymous',
	modifiers: ['default'],
	onInteraction: () => undefined,
};

enum States {
	base = 'a-backdrop',
	visibleState = 'state-a-backdrop--visible',
	transitionState = 'state-a-backdrop--transition',
}

const getModifierClassNameFromTrigger = (trigger?: IBackdropTrigger | void): string => {
	if (trigger && Array.isArray(trigger.modifiers)) {
		return trigger.modifiers.join(' ');
	}

	return '';
};

/**
 * Usage:
 *
 * import { getComponentByDomNode } from '@gondel/core';
 * import { Backdrop } from './backdrop/js/backdrop';
 *
 * Variant #1:
 * 		const backdrop = getComponentByDomNode<Backdrop>($('.js-a-backdrop'));
 * 		backdrop.show(); // or backdrop.hide();
 *
 * Variant #2 (recommended):
 * 		Backdrop.getComponent().show();
 *
 * @author jbiasi <jan.biasi@namics.com>
 * @version 1.0
 */
@Component('Backdrop')
export class Backdrop extends GondelJqueryComponent {
	/**
	 * Static accessor for the backdrop, which is a singleton. Will
	 * also throw errors if there's a miss-configuration with the backdrop.
	 * @returns {Backdrop}		backdrop instance
	 */
	public static getComponent(): Backdrop {
		const backdrops = findComponents<Backdrop>((document as any).documentElement, 'Backdrop');
		if (backdrops.length === 0) {
			throw new Error('No backdrop found, did you forgot to add it into your template?');
		} else if (backdrops.length > 1) {
			throw new Error('More than one backdrop found, please check your markup.');
		}

		return backdrops[0];
	}

	private stack: BackdropStack = [];
	private currentStackIndex = -1;

	/**
	 * Check if the backdrop is visible
	 */
	get isBackdropActive(): boolean {
		return this.currentStackIndex >= 0;
	}

	/**
	 * Gets the last trigger for the backdrop (most likely 'Overlay..')
	 */
	get currentBackdropTrigger(): IBackdropTrigger {
		return this.stack[this.currentStackIndex];
	}

	/**
	 * Initial load handling, adjust internals if visible
	 */
	public start() {
		if (this.$ctx.hasClass(States.visibleState)) {
			this.currentStackIndex++;
			this.stack.push({
				component: 'HTML',
				modifiers: [],
				onInteraction: () => {
					this.hide();
				},
			});
		}

		this.initSlEventBus();
	}

	/**
	 * Shows the backdrop with a trigger (default value set)
	 * @param trigger {IBackdropTrigger}
	 * @returns {number}
	 */
	public show(trigger: IBackdropTrigger = ANONYMOUS_BACKDROP_TRIGGER): number {
		const modifiers = (trigger.modifiers || []).map((modifier: string) => `${States.base}--${modifier}`);
		this._push({
			...trigger,
			modifiers,
		});

		return this.currentStackIndex;
	}

	/**
	 * Hides the backdrop on a certain layer. Default is the current shown layer.
	 * @param layer {number?}		layer index
	 */
	public hide(layer: number = -1) {
		this._shift(layer && layer >= 0 ? layer : this.currentStackIndex);
	}

	/**
	 * Reset the internals
	 */
	public clear() {
		this.stack = [];
		this.currentStackIndex = -1;
	}

	/**
	 * Handle backdrop clicks (anywhere), will be used in other components.
	 */
	@EventListener('click')
	public _onClickBackdropArea() {
		const currentBackdropTrigger = this.currentBackdropTrigger;

		if (currentBackdropTrigger) {
			currentBackdropTrigger.onInteraction.apply(null, ['click', currentBackdropTrigger]);
		}
	}

	private _shift(index: number = this.currentStackIndex) {
		const lastBackdropTrigger = this.currentBackdropTrigger;
		this.stack.splice(index, 1);
		this.currentStackIndex--;
		this.$ctx.removeClass(getModifierClassNameFromTrigger(lastBackdropTrigger));
		this._handleStackChange();
	}

	private _push(trigger: IBackdropTrigger) {
		const hasVisibleClass = this.$ctx.hasClass(States.visibleState);
		this.stack.push(trigger);
		this.currentStackIndex++;
		this._handleStackChange();

		if (this.currentStackIndex >= 0 && !hasVisibleClass) {
			this.$ctx.addClass(`${States.visibleState} ${getModifierClassNameFromTrigger(trigger)}`);
		}
	}

	private _handleStackChange() {
		const hasVisibleClass = this.$ctx.hasClass(States.visibleState);

		if (this.currentStackIndex >= 0 && !hasVisibleClass) {
			this.$ctx.addClass(`${States.visibleState}`);
			this.$ctx.outerWidth(); // Reflow to make transition work
			this.$ctx.addClass(States.transitionState);
		} else if (this.currentStackIndex === -1 && hasVisibleClass) {
			this.$ctx.removeClass(States.transitionState);
			this.$ctx.one('transitionend', () => {
				this.$ctx.removeClass(States.visibleState);
			});
		}
	}

	private async initSlEventBus() {
		const eb = await eventBus();

		eb.on(SLEvent.ShowBackdrop, (meta, payload) => {
			this.show(payload || ANONYMOUS_BACKDROP_TRIGGER);
		});
		eb.on(SLEvent.HideBackdrop, (meta, payload) => {
			this.hide(payload);
		});
	}
}
